import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Google Analytics 4 loader + SPA pageview tracker.
 *
 * - Injects gtag.js exactly once when REACT_APP_GA4_MEASUREMENT_ID is set.
 * - Fires a `page_view` on every React Router navigation so single-page
 *   route changes are counted (gtag's auto-tracking only fires on the
 *   initial document load in an SPA).
 * - Silently no-ops if the env var is missing — no console noise, no
 *   requests, no globals leaked.
 * - Never blocks the app: script is loaded async.
 *
 * Renders nothing. Safe to mount anywhere inside <BrowserRouter>.
 */
const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

let bootstrapped = false;
function bootstrapGa() {
  if (bootstrapped || !GA_ID || typeof window === "undefined") return;
  bootstrapped = true;

  // Standard gtag.js snippet, done imperatively so it doesn't have to live
  // in index.html (avoids editing the shared HTML shell + keeps this tied
  // to the env var).
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // send_page_view: false — we fire page_view manually on every route change
  // below so SPA navigations are recorded correctly.
  gtag("config", GA_ID, { send_page_view: false });
}

export default function GA4Listener() {
  const location = useLocation();

  useEffect(() => {
    bootstrapGa();
  }, []);

  useEffect(() => {
    if (!GA_ID || typeof window === "undefined" || typeof window.gtag !== "function") return;
    // Skip /admin routes so owner navigation doesn't pollute funnel data —
    // matches the exclusion we already apply to the first-party beacon.
    if (location.pathname.startsWith("/admin")) return;
    const path = location.pathname + location.search;
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: document.title || "",
    });
  }, [location.pathname, location.search]);

  return null;
}
